import { ENTITY_STUNNED, type InputSource } from "../net/connection";
import { isAttackClip, type ClipEntry, type ClipName } from "../render/creaturemesh";
import { Gait } from "../sim/movement";
import type { Intent } from "../input";

/** The five states `CActorInstance::CanAct` reads off a body
 *  (`ActorInstanceBattle.cpp:18-35`). */
export interface ActorAffects {
  /** `IsDead`. */
  readonly dead: boolean;
  /** `IsStun`: the stun a body lies in before it dies. */
  readonly stun: boolean;
  /** `IsParalysis`. */
  readonly paralysis: boolean;
  /** `IsFaint`. */
  readonly faint: boolean;
  /** `IsSleep`. */
  readonly sleep: boolean;
}

/** What a body is playing, as the gates below read it: the clip, whether it is
 *  a skill motion (`IsUsingSkill`, `ActorInstanceMotion.cpp:433-451`) and whether
 *  that skill's motion carries `LoopCancelEnable` (`RaceMotionData.cpp:379-381`). */
export interface ActorMotion {
  readonly clip: ClipName | null;
  readonly usingSkill: boolean;
  readonly cancelEnableSkill: boolean;
}

/** `NAME_SPECIAL_1` to `_6` as the pack converts them (`RaceManager.cpp:227-232`). */
const SPECIALS: ReadonlySet<ClipName> = new Set<ClipName>(["special1", "special2", "special3", "special4", "special5"]);

/** A skill clip's name, `skill<slot>g<grade>`, and a rider's with his mounted
 *  mode after it (`skillclip.ts`). */
const SKILL_CLIP = /^skill\d+g\d+(?:Horse[A-Za-z]*)?$/;

/** `CActorInstance::IsUsingSkill` (`ActorInstanceMotion.cpp:433-451`): a motion
 *  from `NAME_SKILL` up to `NAME_SKILL_END`, or a special attack. */
export function isUsingSkill(clip: ClipName | null): boolean {
  if (clip === null) return false;
  if (SKILL_CLIP.test(clip)) return true; // ActorInstanceMotion.cpp:437-438
  return SPECIALS.has(clip); // :440-448
}

/** `CActorInstance::IsFishing` (`ActorInstanceMotion.cpp:454-464`): the wait a
 *  cast line holds and the react a bite plays. A body with no motion is not. */
export function isFishing(clip: ClipName | null): boolean {
  return clip === "fishingWait" || clip === "fishingReact"; // ActorInstanceMotion.cpp:456-461
}

/** `CActorInstance::CanCancelSkill` (`ActorInstanceMotion.cpp:466-470`): the
 *  motion's `LoopCancelEnable` (`RaceMotionData.cpp:379-381`). */
export function canCancelSkill(entry: Pick<ClipEntry, "loopCancel">): boolean {
  return entry.loopCancel === true;
}

/** `CActorInstance::__IsNeedFlyTargetMotion` (`ActorInstanceMotion.cpp:828-851`):
 *  a motion carrying a warp, fly or effect-to-target event, or no motion data at all. */
export function needsFlyTarget(entry: Pick<ClipEntry, "holdsTarget"> | null): boolean {
  if (entry === null) return true; // ActorInstanceMotion.cpp:830-831
  return entry.holdsTarget === true; // :839-846
}

/** What the gates read off a body playing `clip`, whose pack row is `entry`:
 *  `IsUsingSkill`, and `CanCancelSkill` for a skill motion only, as `isLock`
 *  asks it (`ActorInstanceMotion.cpp:535-541`). */
export function actorMotion(clip: ClipName | null, entry: Pick<ClipEntry, "loopCancel"> | null): ActorMotion {
  const usingSkill = isUsingSkill(clip);
  return { clip, usingSkill, cancelEnableSkill: usingSkill && entry !== null && canCancelSkill(entry) };
}

/** The client's own body. Dead is the session's own answer, which counts only a
 *  health the server has sent (`Connection.dead`): a body joined and not yet told
 *  its health is alive. `AFFECT_STUN` is the sleep that blocks acting
 *  (`InstanceBaseEffect.cpp:934-935`), sent as `ENTITY_STUNNED`. Nothing in the
 *  2014 client sets paralysis (`SetParalysis`, `ActorInstance.cpp:127-130`, has no
 *  caller), and faint only the developer binding `chrFaintTest` does
 *  (`PythonCharacterModule.cpp:1087-1105`), so both read false. */
export function selfAffects(dead: boolean, flags: number): ActorAffects {
  return { dead, stun: false, paralysis: false, faint: false, sleep: (flags & ENTITY_STUNNED) !== 0 };
}

/** `CActorInstance::CanAct` (`ActorInstanceBattle.cpp:18-35`). */
export function canAct(affects: ActorAffects): boolean {
  if (affects.dead) return false; // ActorInstanceBattle.cpp:20-21
  if (affects.stun) return false; // :23-24
  if (affects.paralysis) return false; // :26-27
  if (affects.faint) return false; // :29-30
  if (affects.sleep) return false; // :32-33
  return true;
}

/** `NAME_FISHING_THROW` through `NAME_FISHING_FAIL` (`ActorInstanceBattle.cpp:48-53`). */
const FISHING_ACTS: ReadonlySet<ClipName> = new Set<ClipName>([
  "fishingThrow", "fishingWait", "fishingStop", "fishingReact", "fishingCatch", "fishingFail",
]);

/** The emotion motions `isLock` names (`ActorInstanceMotion.cpp:500-529`): the
 *  clap, the dances, the cheers, the ten emotions, and every kiss and slap. */
const GESTURES: ReadonlySet<ClipName> = new Set<ClipName>([
  "clap", "cheers1", "cheers2",
  "dance1", "dance2", "dance3", "dance4", "dance5", "dance6",
  "congratulation", "forgive", "angry", "attractive", "sad", "shy", "cheerup", "banter", "joy",
  "kissWithWarrior", "kissWithAssassin", "kissWithSura", "kissWithShaman",
  "frenchKissWithWarrior", "frenchKissWithAssassin", "frenchKissWithSura", "frenchKissWithShaman",
  "slapHit", "slapHurt",
]);

/** `NAME_WAIT`, `NAME_WALK` and `NAME_RUN` in whichever motion mode the body
 *  stands (`ActorInstanceBattle.cpp:103-106`; the modes, `InstanceBase.cpp:2866-2895`). */
const LOOPS: ReadonlySet<ClipName> = new Set<ClipName>([
  "idle", "walk", "run", "fishingIdle", "fishingWalk", "fishingRun", "weddingIdle", "weddingWalk",
]);

/** `CActorInstance::isLock` (`ActorInstanceMotion.cpp:472-560`): an attack, a
 *  fishing act or an emotion roots its body, and so does a skill that cannot be
 *  cancelled. */
export function isLock(motion: ActorMotion): boolean {
  const clip = motion.clip;
  if (clip !== null && (isAttackClip(clip) || FISHING_ACTS.has(clip) || GESTURES.has(clip))) return true; // :477-531
  if (motion.usingSkill) return !motion.cancelEnableSkill; // :535-541
  return false;
}

/** `CActorInstance::CanUseSkill` (`ActorInstanceBattle.cpp:38-68`). Its early
 *  yes for a fishing act (`:46-55`) is the answer the rest already gives: a body
 *  playing a fishing motion is playing no skill motion (`ActorInstanceMotion.cpp:433-451`). */
export function canUseSkill(affects: ActorAffects, motion: ActorMotion): boolean {
  if (!canAct(affects)) return false; // ActorInstanceBattle.cpp:40-41
  if (motion.usingSkill) return motion.cancelEnableSkill; // :59-65
  return true;
}

/** `CActorInstance::CanMove` (`ActorInstanceBattle.cpp:70-79`). */
export function canMove(affects: ActorAffects, motion: ActorMotion): boolean {
  return canAct(affects) && !isLock(motion); // :72-76
}

/** `CActorInstance::CanAttack` (`ActorInstanceBattle.cpp:81-93`), with
 *  `CanCancelSkill` (`ActorInstanceMotion.cpp:466-470`). */
export function canAttack(affects: ActorAffects, motion: ActorMotion): boolean {
  if (!canAct(affects)) return false; // ActorInstanceBattle.cpp:83-84
  if (motion.usingSkill && !motion.cancelEnableSkill) return false; // :86-90
  return true;
}

/** `CActorInstance::CanFishing` (`ActorInstanceBattle.cpp:95-115`). */
export function canFishing(affects: ActorAffects, motion: ActorMotion): boolean {
  if (!canAct(affects)) return false; // ActorInstanceBattle.cpp:97-98
  if (motion.usingSkill) return false; // :100-101
  return motion.clip !== null && LOOPS.has(motion.clip); // :103-112
}

/** The client's own body as the session knows it: whether the session holds it
 *  dead, and its entity flags. */
export interface OwnBody {
  readonly dead: boolean;
  readonly selfFlags: number;
}

/** The five states for the client's own body, read off the session. */
export function ownAffects(self: OwnBody): ActorAffects {
  return selfAffects(self.dead, self.selfFlags);
}

/** `__CanMove` over one intent (`PythonPlayerInput.cpp:690-708`): a step the
 *  body may not take is a stand, facing where it was asked to face. */
export function gateMove(intent: Intent, affects: ActorAffects, motion: ActorMotion): Intent {
  if (intent.gait === Gait.IDLE || canMove(affects, motion)) return intent;
  return { heading: intent.heading, gait: Gait.IDLE };
}

/** The client's own body and what it is playing, once the session has one. */
export interface OwnActor {
  readonly body: OwnBody;
  readonly motion: ActorMotion;
}

/** `CPythonPlayer::__CanMove` over an input source (`PythonPlayerInput.cpp:690-708`):
 *  every sampled step is gated by the own body's `CanMove`, and `rooted` stands
 *  the body regardless. */
export function gateInput(input: InputSource, own: () => OwnActor | null, rooted: () => boolean): InputSource {
  return {
    sample: () => {
      const intent = input.sample();
      if (rooted()) return { heading: intent.heading, gait: Gait.IDLE };
      if (intent.gait === Gait.IDLE) return intent;
      const actor = own();
      if (actor === null) return { heading: intent.heading, gait: Gait.IDLE }; // no main actor, PythonPlayerInput.cpp:697-699
      return gateMove(intent, ownAffects(actor.body), actor.motion);
    },
  };
}
